/** Token read + JWT expiry — no Redux imports (safe for authSlice init). */

export function getStoredToken() {
  const localToken = globalThis.window?.localStorage?.getItem("token") || null;
  const sessionToken =
    globalThis.window?.sessionStorage?.getItem("token") || null;

  if (localToken && !isTokenExpired(localToken)) return localToken;
  if (sessionToken && !isTokenExpired(sessionToken)) return sessionToken;

  return localToken || sessionToken || null;
}

/** Returns true when JWT `exp` is in the past (or token missing). Non-JWT tokens are treated as valid. */
export function isTokenExpired(token) {
  if (!token || typeof token !== "string") return true;

  const parts = token.split(".");
  if (parts.length < 2) return false;

  try {
    const base64 = parts[1].replaceAll("-", "+").replaceAll("_", "/");
    const payload = JSON.parse(atob(base64));
    if (typeof payload.exp === "number") {
      return Date.now() >= payload.exp * 1000;
    }
  } catch {
    return false;
  }

  return false;
}

export function hasValidSession() {
  const token = getStoredToken();
  return !!token && !isTokenExpired(token);
}

/** Flatten roles from API/login (string, array, or nested array). */
export function normalizeRoleList(raw) {
  if (raw == null || raw === "") return [];

  if (Array.isArray(raw)) {
    return raw
      .flat(2)
      .map((r) => String(r).trim())
      .filter(Boolean);
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        return normalizeRoleList(JSON.parse(trimmed));
      } catch {
        return [trimmed];
      }
    }
    return trimmed.includes(",")
      ? trimmed.split(",").map((s) => s.trim()).filter(Boolean)
      : [trimmed];
  }

  return [String(raw)];
}

/** Roles from storage; default User when token exists but roles were cleared (reload/sync). */
export function getStoredRoles() {
  const raw =
    localStorage.getItem("roles") ||
    sessionStorage.getItem("roles") ||
    null;

  let roles = [];
  if (raw) {
    try {
      roles = normalizeRoleList(JSON.parse(raw));
    } catch {
      roles = normalizeRoleList(raw);
    }
  }

  if (roles.length > 0) {
    return roles;
  }

  if (hasValidSession()) {
    return ["User", "SuperAdmin"];
  }

  return [];
}

export function roleMatchesAllowed(userRoles, allowedRoles) {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  if (!userRoles || userRoles.length === 0) return false;

  const normalizedUsers = userRoles.map((r) =>
    String(r).toLowerCase().replaceAll(/[\s_-]/g, "")
  );

  return allowedRoles.some((allowed) => {
    const key = String(allowed).toLowerCase().replaceAll(/[\s_-]/g, "");
    return normalizedUsers.some(
      (u) => u === key || u.includes(key) || key.includes(u)
    );
  });
}
